import styled from "@emotion/styled"
import { get } from "lodash"
import React from "react"
import tw from "twin.macro"
import linkTransformer from "../utils/linkTransformer"
import Button from "./Button"
import Link from "./Link"
import Picture from "./Picture"
import VideoEmbed from "./VideoEmbed"

const StyledMediaGrid = styled.div`
  ${tw`flex flex-wrap justify-center -ml-2 -mt-2`}
`
const MediaGridColumn = styled.div`
  ${tw`max-w-full sm:w-1/2 lg:w-1/3 pl-2 mt-2`}
  width: 418px;
`
const ResponsiveWrapper = styled.div`
  ${tw`w-full bg-teal relative`}
  padding-bottom: 56.25%;
`
const MediaGridItem = styled.div`
  ${tw`inset-0 w-full h-full absolute overflow-hidden`}
  ${(props) =>
    props.backgroundColor &&
    {
      blue: tw`bg-blue text-white`,
      teal: tw`bg-teal text-white`,
      white: tw`bg-white text-gray border border-gray`,
    }[props.backgroundColor]}
  &.has-text {
    ${tw`flex items-center justify-center`}
  }
  img {
    ${tw`w-full h-full object-cover`}
  }
  .c-bg-image {
    ${tw`absolute inset-0 w-full h-full object-cover object-center`}
  }
`
const Text = styled.div`
  ${tw`relative z-10 w-full p-4 md:p-8 text-center text-sm xl:text-base`}
  h3 {
    ${tw`font-bold text-lg xl:text-xl uppercase`}
  }
  * + a {
    ${tw`mt-2`}
  }
`
const MediaGridBlock = ({ block }) => {
  const mediaGridItems = get(block, "elements.media_grid_items.value")

  return (
    <StyledMediaGrid>
      {mediaGridItems.map((mediaGridItem) => {
        const type = get(mediaGridItem, "system.type")
        const id = get(mediaGridItem, "system.id")

        return (
          <MediaGridColumn key={id}>
            <ResponsiveWrapper>
              {
                {
                  media_grid_item_image: (
                    <MediaGridItemImage item={mediaGridItem} />
                  ),
                  media_grid_item_text: (
                    <MediaGridItemText item={mediaGridItem} />
                  ),
                  media_grid_item_video: (
                    <MediaGridItemVideo item={mediaGridItem} />
                  ),
                }[type]
              }
            </ResponsiveWrapper>
          </MediaGridColumn>
        )
      })}
    </StyledMediaGrid>
  )
}

export default MediaGridBlock

const MediaGridItemImage = ({ item }) => {
  const image = get(
    item,
    "elements.image.value[0].elements.image.value[0]"
  )
  const altText = get(
    item,
    "elements.image.value[0].elements.alt_text.value"
  )

  const transformedLink = linkTransformer(item)

  const markup = () => {
    return (
      <MediaGridItem>
        <Picture
          image={image}
          cropWidth={410}
          cropRatio={0.5375}
          cover={true}
          alt={altText}
        />
      </MediaGridItem>
    )
  }

  return transformedLink.to ? (
    <Link {...transformedLink}>{markup()}</Link>
  ) : (
    markup()
  )
}

const MediaGridItemText = ({ item }) => {
  const backgroundImage = get(
    item,
    "elements.background_image.value[0].elements.image.value[0]"
  )
  const backgroundColor = get(
    item,
    "elements.background_color.value[0].codename"
  )
  const headingColor = get(item, "elements.heading_color.value[0].codename")
  const heading = get(item, "elements.heading.value")
  const altText = get(
    item,
    "elements.image.value[0].elements.alt_text.value"
  )
  const text = get(item, "elements.text.value")
  const transformedLink = linkTransformer(item)
  const contrastText =
    backgroundColor && ["teal", "blue"].indexOf(backgroundColor) !== -1
      ? true
      : false

  return (
    <MediaGridItem
      backgroundColor={backgroundColor}
      headingColor={headingColor}
      className="has-text"
    >
      <Text>
        {heading ? <h3>{heading}</h3> : null}
        {text ? <p>{text}</p> : null}
        {transformedLink.to ? (
          <Button
            type="underline"
            contrastText={contrastText}
            to={transformedLink.to}
            openInNewWindow={transformedLink.openInNewWindow}
            as={Link}
          >
            {transformedLink.label}
          </Button>
        ) : null}
      </Text>
      {backgroundImage ? (
        <Picture
          image={backgroundImage}
          cropWidth={410}
          cropRatio={0.5375}
          alt={altText}
          className="c-bg-image"
        />
      ) : null}
    </MediaGridItem>
  )
}

const MediaGridItemVideo = ({ item }) => {
  const video = get(item, "elements.video.value[0]")

  return (
    <MediaGridItem>
      <VideoEmbed video={video} playInModal={true} />
    </MediaGridItem>
  )
}
